// =============================================================================
// SWMS PDF export — T-427b
// =============================================================================
// Clean landscape, Coretel-branded, controlled-document framing — the thing
// you hand a client HSE manager when they ask for the SWMS. Renders the
// structured content (steps × hazards × risk before/after × controls) plus a
// blank sign-on table for wet-ink days.
// pdfmake lazy-load pattern cribbed from projectFinancialsExport (T-395 era).
// =============================================================================

import pdfmakeScriptUrl from "pdfmake/build/pdfmake.min.js?url";
// @ts-ignore vfs fonts has no types
import vfsFontsScriptUrl from "pdfmake/build/vfs_fonts.js?url";

const C = {
  accent: "#FF6A1A",
  ink: "#111111",
  text: "#222222",
  muted: "#666666",
  panel: "#F6F6F4",
  rule: "#E2E1DD",
  low: "#1D8348",
  medium: "#B7950B",
  high: "#CB4335",
  extreme: "#922B21",
};

function loadScriptOnce(src: string, tag: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[data-pdfmake-tag="${tag}"]`);
    if (existing) {
      if (existing.dataset.loaded === "true") resolve();
      else { existing.addEventListener("load", () => resolve()); existing.addEventListener("error", () => reject(new Error(`reload ${tag}`))); }
      return;
    }
    const s = document.createElement("script");
    s.src = src; s.async = true; s.dataset.pdfmakeTag = tag;
    s.onload = () => { s.dataset.loaded = "true"; resolve(); };
    s.onerror = () => reject(new Error(`load ${tag}`));
    document.head.appendChild(s);
  });
}

async function getPdfMake(): Promise<any> {
  const existing = (window as any).pdfMake;
  if (existing?.vfs && Object.keys(existing.vfs).length > 0) return existing;
  await loadScriptOnce(pdfmakeScriptUrl, "pdfmake-core");
  await loadScriptOnce(vfsFontsScriptUrl, "pdfmake-vfs");
  const pdfMake = (window as any).pdfMake;
  if (!pdfMake) throw new Error("pdfMake missing");
  return pdfMake;
}

// =============================================================================
// T-475d — STATE-SPECIFIC REQUIREMENTS & CONTACTS (James, 08/08: "where are the
// state specific legislations... environmental contact for the state").
// This is LOCKED Layer-2 compliance data rendered at export time, keyed by the
// site's state — never AI-generated, so a number can't be hallucinated. Keep it
// current here; the footnote tells crews to confirm at induction.
// =============================================================================
const NATIONAL_CONTACTS: [string, string][] = [
  ["EMERGENCY", "000 (112 from mobile) — police / fire / ambulance"],
  ["Before You Dig", "Before You Dig Australia (Dial Before You Dig) — 1100 · byda.com.au — mandatory before any ground disturbance"],
];

const STATE_DIRECTORY: Record<string, [string, string][]> = {
  QLD: [
    ["WHS regulator", "Workplace Health and Safety Queensland (WHSQ) — 1300 362 128"],
    ["Notifiable incidents", "WHS Act 2011 (Qld) s38 — make the site safe WITHOUT disturbing the scene and notify WHSQ (1300 362 128) immediately for a death, serious injury/illness or dangerous incident"],
    ["Electrical network", "Energex (SEQ): emergencies/fallen lines 13 19 62 · faults 13 62 62 · Ergon Energy (regional): 13 16 70 · electrical incidents also to the Electrical Safety Office via WHSQ"],
    ["Environmental duty", "General environmental duty — Environmental Protection Act 1994 (Qld) · pollution hotline 1300 130 372 · spill kit on site; contain, then report"],
  ],
  NSW: [
    ["WHS regulator", "SafeWork NSW — 13 10 50"],
    ["Notifiable incidents", "WHS Act 2011 (NSW) s38 — preserve the scene and notify SafeWork NSW (13 10 50) immediately"],
    ["Electrical network", "Ausgrid 13 13 88 · Endeavour Energy 131 003 · Essential Energy 13 20 80 (network operator per site)"],
    ["Environmental duty", "Protection of the Environment Operations Act 1997 (NSW) · EPA Environment Line 131 555 · contain spills, then report"],
  ],
  SA: [
    ["WHS regulator", "SafeWork SA — 1300 365 255"],
    ["Notifiable incidents", "WHS Act 2012 (SA) s38 — preserve the scene and notify SafeWork SA (1300 365 255) immediately"],
    ["Electrical network", "SA Power Networks — faults and emergencies 13 13 66"],
    ["Environmental duty", "Environment Protection Act 1993 (SA) — contain spills, then report to the EPA (SA)"],
  ],
  WA: [
    ["WHS regulator", "WorkSafe WA — 1300 307 877"],
    ["Notifiable incidents", "WHS Act 2020 (WA) s38 — preserve the scene and notify WorkSafe WA (1300 307 877) immediately"],
    ["Electrical network", "Western Power (SWIS) 13 13 51 · Horizon Power (regional) 13 23 51"],
    ["Environmental duty", "Environmental Protection Act 1986 (WA) — contain spills, then report to DWER"],
  ],
  VIC: [
    ["WHS regulator", "WorkSafe Victoria — 13 23 60 (OHS Act 2004 — Victoria is NOT WHS-harmonised)"],
    ["Notifiable incidents", "OHS Act 2004 (Vic) Part 5 — preserve the scene and notify WorkSafe Victoria (13 23 60) immediately"],
    ["Electrical network", "Distributor per site (AusNet / CitiPower / Powercor / Jemena / United Energy) — confirm at induction"],
    ["Environmental duty", "Environment Protection Act 2017 (Vic) — general environmental duty · EPA Victoria 1300 372 842"],
  ],
  TAS: [
    ["WHS regulator", "WorkSafe Tasmania — 1300 366 322"],
    ["Notifiable incidents", "WHS Act 2012 (Tas) s38 — preserve the scene and notify WorkSafe Tasmania (1300 366 322) immediately"],
    ["Electrical network", "TasNetworks — 132 004"],
    ["Environmental duty", "Environmental Management and Pollution Control Act 1994 (Tas) — contain spills, then report"],
  ],
  NT: [
    ["WHS regulator", "NT WorkSafe — 1800 019 115"],
    ["Notifiable incidents", "WHS (NUL) Act 2011 (NT) s38 — preserve the scene and notify NT WorkSafe (1800 019 115) immediately"],
    ["Electrical network", "Power and Water Corporation — confirm current fault line at induction"],
    ["Environmental duty", "Environment Protection Act 2019 (NT) — contain spills, then report"],
  ],
  ACT: [
    ["WHS regulator", "WorkSafe ACT — 13 22 81"],
    ["Notifiable incidents", "WHS Act 2011 (ACT) s38 — preserve the scene and notify WorkSafe ACT (13 22 81) immediately"],
    ["Electrical network", "Evoenergy — 13 10 93"],
    ["Environmental duty", "Environment Protection Act 1997 (ACT) — contain spills, then report"],
  ],
};

// T-475e — the classic hierarchy-of-controls pyramid (inverted: most effective
// at the top). Drawn as stepped bands so it renders identically everywhere —
// no image asset to load or lose. Pairs with the risk-rating key on the method
// page, which is also how commercial SWMS packs present it (James, 08/08).
function hierarchyPyramid(): any {
  const bands: [string, string, string][] = [
    ["1. ELIMINATION", "remove the hazard entirely", "#1D8348"],
    ["2. SUBSTITUTION", "replace with something safer", "#3F8F4F"],
    ["3. ISOLATION", "separate people from the hazard", "#B7950B"],
    ["4. ENGINEERING", "physical controls — guards, barriers, platforms", "#D68910"],
    ["5. ADMINISTRATIVE", "procedures, training, supervision, signage", "#CB4335"],
    ["6. PPE", "last line of defence — never the only control", "#922B21"],
  ];
  const full = 330;
  return {
    stack: [
      { text: "MOST EFFECTIVE", fontSize: 6, color: C.muted, alignment: "center", characterSpacing: 1, margin: [0, 0, 0, 2] },
      ...bands.map(([t, d, col], i) => {
        const w = full - i * 42;
        const pad = (full - w) / 2;
        return {
          table: { widths: [pad, w, pad], body: [[
            { text: "", border: [false, false, false, false] },
            { text: [{ text: `${t}  `, bold: true }, { text: d }], fontSize: 6.5, color: "#ffffff", fillColor: col, alignment: "center", margin: [2, 2.5, 2, 2.5] },
            { text: "", border: [false, false, false, false] },
          ]] },
          layout: "noBorders",
        };
      }),
      { text: "LEAST EFFECTIVE", fontSize: 6, color: C.muted, alignment: "center", characterSpacing: 1, margin: [0, 2, 0, 0] },
    ],
  };
}

function riskCell(label?: string) {
  const colour = label === "Low" ? C.low : label === "Medium" ? C.medium : label === "High" ? C.high : label === "Extreme" ? C.extreme : C.muted;
  return { text: label ?? "—", color: "#ffffff", fillColor: colour, alignment: "center", bold: true, fontSize: 7.5 };
}

export async function exportSwmsPdf(opts: {
  swmsNumber: string;
  title: string;
  version: number;
  status: string;
  projectCode?: string | null;
  projectName?: string | null;
  siteName?: string | null;
  clientName?: string | null;
  approvedBy?: string | null;
  approvedAt?: string | null;
  baseTemplate?: string | null;
  state?: string | null;          // site's state — keys the locked contacts panel
  content: any;
  // mig 408 (30 Sep 2026) — the front matter a principal's auditor looks for:
  // valid-to, the reason for this revision, and who was consulted.
  validTo?: string | null;
  reviewReason?: string | null;
  consulted?: { name: string; company?: string; reviewed_at: string; kind?: string; comment?: string | null; considered?: boolean; response?: string | null }[] | null;
  reacceptAfter?: string | null;
  reacceptReason?: string | null;
}) {
  const pdfMake = await getPdfMake();
  const c = opts.content ?? {};
  const fmt = (d?: string | null) => d ? new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }) : "—";
  // mig 408 — structured permits & checks: [{ name, required, note }]; an old
  // document only has permits_required (free text) and prints that instead.
  const checks: { name: string; required: boolean; note?: string }[] = Array.isArray(c.permits_checks) ? c.permits_checks : [];
  const checksOn = checks.filter(x => x.required);
  const consulted = Array.isArray(opts.consulted) ? opts.consulted : [];

  const stepRows: any[] = [];
  (c.job_steps ?? []).forEach((st: any, i: number) => {
    (st.hazards ?? []).forEach((h: any, hi: number) => {
      stepRows.push([
        // No rowSpan: when a step's hazards broke across a page, the spanned
        // cell stayed behind and the continuation rows floated with an EMPTY
        // job-step cell (v1.6 page 4). Every row now carries its context —
        // continuations dimmed so the first row still reads as the step.
        hi === 0 ? { text: `${i + 1}. ${st.step}`, bold: true, fontSize: 8 }
                 : { text: `${i + 1}. (cont.)`, italics: true, fontSize: 6.5, color: C.muted },
        { text: h.hazard, fontSize: 8 },
        riskCell(h.risk_before),
        { ul: (h.controls ?? []).map((x: string) => ({ text: x, fontSize: 7.5 })), margin: [0, 0, 0, 0] },
        riskCell(h.risk_after),
        { text: h.responsible ?? "", fontSize: 8 },
      ]);
    });
    if ((st.hazards ?? []).length === 0) {
      stepRows.push([{ text: `${i + 1}. ${st.step}`, bold: true, fontSize: 8 }, "", "", "", "", ""]);
    }
  });

  const signRows = Array.from({ length: 10 }, () => [
    { text: " ", margin: [0, 7, 0, 7] }, " ", " ", " ",
  ]);

  const dd: any = {
    pageSize: "A4",
    pageOrientation: "landscape",
    pageMargins: [28, 64, 28, 40],
    defaultStyle: { fontSize: 9, color: C.text },
    header: {
      margin: [28, 18, 28, 0],
      columns: [
        {
          stack: [
            { text: "CORETEL GROUP", bold: true, fontSize: 13, color: C.ink, characterSpacing: 0.5 },
            { text: "SAFE WORK METHOD STATEMENT", fontSize: 7.5, color: C.accent, characterSpacing: 1.5 },
          ],
        },
        {
          stack: [
            { text: opts.swmsNumber, alignment: "right", bold: true, fontSize: 10, color: C.accent },
            { text: `Version ${opts.version} · ${opts.status === "approved" ? "APPROVED" : "DRAFT — NOT FOR USE"}`, alignment: "right", fontSize: 7.5, color: opts.status === "approved" ? C.muted : C.high },
          ],
        },
      ],
    },
    footer: (page: number, pages: number) => ({
      margin: [28, 8, 28, 0],
      columns: [
        { text: "Printed/downloaded copies are UNCONTROLLED — the current version lives in the Coretel operations system.", fontSize: 6.5, color: C.muted },
        { text: `Page ${page} of ${pages}`, alignment: "right", fontSize: 7, color: C.muted },
      ],
    }),
    content: [
      { text: opts.title, bold: true, fontSize: 14, color: C.ink, margin: [0, 0, 0, 6] },
      {
        table: {
          widths: ["auto", "*", "auto", "*", "auto", "*"],
          body: [[
            { text: "PROJECT", bold: true, fontSize: 7, color: C.muted }, { text: `${opts.projectCode ?? "—"} · ${opts.projectName ?? ""}`, fontSize: 8.5 },
            { text: "SITE", bold: true, fontSize: 7, color: C.muted }, { text: opts.siteName ?? "—", fontSize: 8.5 },
            { text: "CLIENT", bold: true, fontSize: 7, color: C.muted }, { text: opts.clientName ?? "—", fontSize: 8.5 },
          ], [
            { text: "APPROVED BY", bold: true, fontSize: 7, color: C.muted }, { text: opts.approvedBy ?? "—", fontSize: 8.5 },
            { text: "APPROVED", bold: true, fontSize: 7, color: C.muted }, { text: fmt(opts.approvedAt), fontSize: 8.5 },
            { text: "BASE TEMPLATE", bold: true, fontSize: 7, color: C.muted }, { text: opts.baseTemplate ?? "—", fontSize: 8.5 },
          ], [
            // mig 408 — the control record
            { text: "VALID TO", bold: true, fontSize: 7, color: C.muted }, { text: opts.approvedAt ? fmt(opts.validTo) : "— (on approval)", fontSize: 8.5 },
            { text: "REVISION", bold: true, fontSize: 7, color: C.muted }, { text: `v${opts.version}${opts.reviewReason ? ` — ${opts.reviewReason}` : ""}`, fontSize: 8.5 },
            { text: "RESCUE PLAN", bold: true, fontSize: 7, color: C.muted }, { text: c.rescue_plan_required === true ? "REQUIRED — separate document, in place before the climb" : c.rescue_plan_required === false ? "Not required for this work" : "—", fontSize: 8.5, bold: c.rescue_plan_required === true },
          ]],
        },
        layout: { fillColor: () => C.panel, hLineWidth: () => 0, vLineWidth: () => 0, paddingTop: () => 4, paddingBottom: () => 4, paddingLeft: () => 6, paddingRight: () => 6 },
        margin: [0, 0, 0, 8],
      },
      { text: "SCOPE OF WORK", bold: true, fontSize: 8, color: C.accent, margin: [0, 4, 0, 2] },
      { text: c.scope_of_work ?? "—", fontSize: 8.5, margin: [0, 0, 0, 6] },
      ...(c.hrcw_categories?.length ? [
        { text: "HIGH-RISK CONSTRUCTION WORK (WHS Reg 291)", bold: true, fontSize: 8, color: C.accent, margin: [0, 2, 0, 2] },
        {
          columns: [
            { width: "*", ul: c.hrcw_categories.slice(0, Math.ceil(c.hrcw_categories.length / 2)).map((x: string) => ({ text: x, fontSize: 8 })) },
            { width: "*", ul: c.hrcw_categories.slice(Math.ceil(c.hrcw_categories.length / 2)).map((x: string) => ({ text: x, fontSize: 8 })) },
          ],
          columnGap: 14, margin: [0, 0, 0, 6],
        },
      ] : []),
      // mig 408 — training / competencies, permits & checks (structured, with "SDS required"), hazardous substances
      ...((c.training_required?.length || checks.length || c.hazardous_substances?.length) ? [{
        columns: [
          ...(c.training_required?.length ? [{ width: "*", stack: [
            { text: "TRAINING, LICENCES & COMPETENCIES REQUIRED", bold: true, fontSize: 8, color: C.accent, margin: [0, 2, 0, 2] },
            { ul: c.training_required.map((x: string) => ({ text: x, fontSize: 8 })) },
            { text: "Verified on the sign-on / compliance matrix before work starts.", fontSize: 6.5, color: C.muted, margin: [0, 2, 0, 0] },
          ]}] : []),
          ...(checks.length ? [{ width: "*", stack: [
            { text: "PERMITS & CHECKS", bold: true, fontSize: 8, color: C.accent, margin: [0, 2, 0, 2] },
            { table: { widths: ["auto", "*"], body: checks.map(x => [
                { text: x.required ? "\u2612" : "\u2610", fontSize: 9, bold: x.required, color: x.required ? C.ink : C.muted },
                { text: [{ text: x.name, fontSize: 8, bold: x.required, color: x.required ? C.ink : C.muted }, ...(x.note ? [{ text: ` — ${x.note}`, fontSize: 7.5, color: C.muted }] : [])] },
              ]) },
              layout: "noBorders" },
          ]}] : []),
          ...(c.hazardous_substances?.length ? [{ width: "*", stack: [
            { text: "HAZARDOUS SUBSTANCES — SDS HELD", bold: true, fontSize: 8, color: C.accent, margin: [0, 2, 0, 2] },
            { ul: c.hazardous_substances.map((x: string) => ({ text: x, fontSize: 8 })) },
            { text: "Sheets on the company SDS register (HSE portal) and in the site folder.", fontSize: 6.5, color: C.muted, margin: [0, 2, 0, 0] },
          ]}] : []),
        ],
        columnGap: 14,
        margin: [0, 0, 0, 8],
      }] : []),
      {
        columns: [
          ...(c.ppe?.length ? [{ width: "*", stack: [
            { text: c.ppe_task_specific?.length ? "PPE — MANDATORY ON SITE" : "PPE REQUIRED", bold: true, fontSize: 8, color: C.accent, margin: [0, 2, 0, 2] },
            { ul: c.ppe.map((x: string) => ({ text: x, fontSize: 8 })) },
          ]}] : []),
          ...(c.ppe_task_specific?.length ? [{ width: "*", stack: [
            { text: "PPE — TASK SPECIFIC", bold: true, fontSize: 8, color: C.accent, margin: [0, 2, 0, 2] },
            { ul: c.ppe_task_specific.map((x: string) => ({ text: x, fontSize: 8 })) },
          ]}] : []),
          ...(!checks.length && c.permits_required?.length ? [{ width: "*", stack: [
            { text: "PERMITS REQUIRED", bold: true, fontSize: 8, color: C.accent, margin: [0, 2, 0, 2] },
            { ul: c.permits_required.map((x: string) => ({ text: x, fontSize: 8 })) },
          ]}] : []),
          ...(c.plant?.length ? [{ width: "*", stack: [
            { text: "PLANT & EQUIPMENT", bold: true, fontSize: 8, color: C.accent, margin: [0, 2, 0, 2] },
            { ul: c.plant.map((x: string) => ({ text: x, fontSize: 8 })) },
          ]}] : []),
          ...(c.referenced_legislation?.length ? [{ width: "*", stack: [
            { text: "LEGISLATION & CODES", bold: true, fontSize: 8, color: C.accent, margin: [0, 2, 0, 2] },
            { ul: c.referenced_legislation.map((x: string) => ({ text: x, fontSize: 8 })) },
          ]}] : []),
        ],
        columnGap: 14,
        margin: [0, 0, 0, 8],
      },
      ...(opts.state && STATE_DIRECTORY[opts.state.toUpperCase()] ? [
        { text: `STATE-SPECIFIC REQUIREMENTS & CONTACTS — ${opts.state.toUpperCase()}`, bold: true, fontSize: 8, color: C.accent, margin: [0, 4, 0, 2] },
        {
          table: { widths: [110, "*"], body: [
            ...NATIONAL_CONTACTS.map(([k, v]) => [{ text: k, bold: true, fontSize: 7 }, { text: v, fontSize: 7 }]),
            ...STATE_DIRECTORY[opts.state.toUpperCase()].map(([k, v]) => [{ text: k, bold: true, fontSize: 7 }, { text: v, fontSize: 7 }]),
          ] },
          layout: { hLineColor: () => C.rule, vLineColor: () => C.rule, hLineWidth: () => 0.5, vLineWidth: () => 0.5,
            paddingTop: () => 2, paddingBottom: () => 2, paddingLeft: () => 3, paddingRight: () => 3 },
        },
        { text: "Fixed directory maintained in the Coretel operations system — not AI-generated. Confirm numbers are current at induction.", fontSize: 6, color: C.muted, margin: [0, 2, 0, 6] },
      ] : []),
      // Page 1 = summary (scope, HRCW, lists, state contacts). Page 2 opens the
      // method with the RISK KEY and the HIERARCHY PYRAMID side by side — the
      // legend used to sit on page 1 and split across the break, stranding two
      // rows on an otherwise blank page 2 (James, 08/08).
      { text: "", pageBreak: "before" },
      {
        columns: [
          { width: "*", stack: [
            { text: "RISK RATING — qualitative likelihood × consequence", bold: true, fontSize: 8, color: C.accent, margin: [0, 0, 0, 2] },
            {
              table: { widths: ["auto", "*"], body: [
                [{ text: "EXTREME", bold: true, fontSize: 7, color: "#fff", fillColor: "#9a3324", alignment: "center", margin: [2, 1, 2, 1] },
                 { text: "Fatality or permanent disabling injury is a credible outcome — work does not proceed without director-level review of the controls.", fontSize: 7 }],
                [{ text: "HIGH", bold: true, fontSize: 7, color: "#fff", fillColor: "#c0392b", alignment: "center", margin: [2, 1, 2, 1] },
                 { text: "Serious injury (hospitalisation, lost time) is credible — controls must actively reduce likelihood before work proceeds.", fontSize: 7 }],
                [{ text: "MEDIUM", bold: true, fontSize: 7, color: "#fff", fillColor: "#b7950b", alignment: "center", margin: [2, 1, 2, 1] },
                 { text: "Injury requiring first aid / medical treatment is possible — proceed with the stated controls in place.", fontSize: 7 }],
                [{ text: "LOW", bold: true, fontSize: 7, color: "#fff", fillColor: "#1e8449", alignment: "center", margin: [2, 1, 2, 1] },
                 { text: "Minor injury at most — managed by routine controls and general site rules.", fontSize: 7 }],
              ] },
              layout: { hLineColor: () => C.rule, vLineColor: () => C.rule, hLineWidth: () => 0.5, vLineWidth: () => 0.5,
                paddingTop: () => 2, paddingBottom: () => 2, paddingLeft: () => 3, paddingRight: () => 3 },
            },
            { text: "Residual risk reflects the stated controls honestly. Any residual HIGH or EXTREME is a formal, documented acceptance by the approving director — not an oversight.", fontSize: 6.5, color: C.muted, margin: [0, 3, 0, 0] },
          ] },
          { width: 340, stack: [
            { text: "HIERARCHY OF CONTROLS", bold: true, fontSize: 8, color: C.accent, alignment: "center", margin: [0, 0, 0, 2] },
            hierarchyPyramid(),
            { text: "Controls in the method below are listed hierarchy-first.", fontSize: 6, color: C.muted, alignment: "center", margin: [0, 2, 0, 0] },
          ] },
        ],
        columnGap: 20,
        margin: [0, 0, 0, 10],
      },
      { text: "METHOD — JOB STEPS, HAZARDS & CONTROLS", bold: true, fontSize: 9, color: C.accent, margin: [0, 4, 0, 4] },
      {
        table: {
          headerRows: 1,
          dontBreakRows: true,
          keepWithHeaderRows: 1,
          widths: [130, 120, 38, "*", 38, 70],
          body: [
            [
              { text: "JOB STEP", bold: true, fontSize: 7.5, color: "#fff", fillColor: C.ink },
              { text: "HAZARD", bold: true, fontSize: 7.5, color: "#fff", fillColor: C.ink },
              { text: "RISK", bold: true, fontSize: 7.5, color: "#fff", fillColor: C.ink, alignment: "center" },
              { text: "CONTROLS (hierarchy first)", bold: true, fontSize: 7.5, color: "#fff", fillColor: C.ink },
              { text: "RESID.", bold: true, fontSize: 7.5, color: "#fff", fillColor: C.ink, alignment: "center" },
              { text: "RESPONSIBLE", bold: true, fontSize: 7.5, color: "#fff", fillColor: C.ink },
            ],
            ...stepRows,
          ],
        },
        layout: {
          hLineColor: () => C.rule, vLineColor: () => C.rule,
          hLineWidth: () => 0.5, vLineWidth: () => 0.5,
          paddingTop: () => 3, paddingBottom: () => 3, paddingLeft: () => 4, paddingRight: () => 4,
        },
      },
      ...(c.emergency_notes ? [
        { text: "EMERGENCY", bold: true, fontSize: 8, color: C.accent, margin: [0, 8, 0, 2] },
        { text: c.emergency_notes, fontSize: 8.5 },
      ] : []),
      // mig 408 — consultation record, review triggers, management of change
      {
        unbreakable: true,
        margin: [0, 10, 0, 0],
        stack: [
          { text: "CONSULTATION — WORKERS CONSULTED ON THIS VERSION (WHS Act s 47–49)", bold: true, fontSize: 8, color: C.accent, margin: [0, 0, 0, 2] },
          ...(consulted.length ? [{
            table: { headerRows: 1, widths: ["*", 60, 70, "*"], body: [
              [
                { text: "NAME", bold: true, fontSize: 7, color: "#fff", fillColor: C.ink },
                { text: "COMPANY", bold: true, fontSize: 7, color: "#fff", fillColor: C.ink },
                { text: "DATE", bold: true, fontSize: 7, color: "#fff", fillColor: C.ink },
                { text: "COMMENT / CHANGE REQUESTED → OUTCOME", bold: true, fontSize: 7, color: "#fff", fillColor: C.ink },
              ],
              ...consulted.map(x => [
                { text: x.name || "—", fontSize: 7.5 },
                { text: x.company || "Coretel", fontSize: 7.5 },
                { text: fmt(x.reviewed_at), fontSize: 7.5 },
                { text: x.comment ? `${x.comment}${x.considered ? ` → ${x.response || "considered"}` : " → open"}` : (x.kind === "reviewed" ? "Read, no comment" : "—"), fontSize: 7.5 },
              ]),
            ] },
            layout: { hLineColor: () => C.rule, vLineColor: () => C.rule, hLineWidth: () => 0.5, vLineWidth: () => 0.5 },
          }] : [
            { text: opts.approvedAt ? "No worker reviews were recorded for this version in the Coretel operations system." : "Recorded at approval from the reviews the crew leave in the Coretel operations system (site folder → SWMS → I've reviewed this).", fontSize: 7.5, color: C.muted },
          ]),
          { text: "Consultation is led by a Coretel director or manager; the record is of the workers consulted, when, and what changed as a result.", fontSize: 6.5, color: C.muted, margin: [0, 2, 0, 6] },
          { text: "RE-REVIEW AND RE-INDUCTION", bold: true, fontSize: 8, color: C.accent, margin: [0, 2, 0, 2] },
          { text: "This SWMS is reviewed, and every worker re-accepts it, when: a significant change to the activity or the site is identified · an incident or near miss occurs relating to the activity · a hazard is identified that this SWMS or the start card does not cover · the valid-to date is reached. Unplanned events, new steps, changed conditions and extra controls found on the day are recorded on that day's start card (management of change) and brought back into this document at the next revision.", fontSize: 7.5 },
          ...(opts.reacceptAfter ? [{ text: `Re-acceptance required from ${fmt(opts.reacceptAfter)}: ${opts.reacceptReason ?? ""}`, fontSize: 7.5, bold: true, color: C.high, margin: [0, 2, 0, 0] }] : []),
        ],
      },
      {
        // No forced page break — the sign-on now shares the page with EMERGENCY
        // when it fits (page 5 used to be the emergency paragraph alone on 90%
        // white space). unbreakable: it moves to the next page WHOLE if short.
        unbreakable: true,
        margin: [0, 16, 0, 0],
        stack: [
          { text: "SIGN-ON — I have read and understood this SWMS and will carry out the work in accordance with it.", bold: true, fontSize: 9, color: C.ink, margin: [0, 0, 0, 6] },
          {
            table: {
              headerRows: 1,
              widths: ["*", "*", 120, 90],
              body: [
                [
                  { text: "NAME", bold: true, fontSize: 7.5, color: "#fff", fillColor: C.ink },
                  { text: "COMPANY", bold: true, fontSize: 7.5, color: "#fff", fillColor: C.ink },
                  { text: "SIGNATURE", bold: true, fontSize: 7.5, color: "#fff", fillColor: C.ink },
                  { text: "DATE", bold: true, fontSize: 7.5, color: "#fff", fillColor: C.ink },
                ],
                ...signRows,
              ],
            },
            layout: {
              hLineColor: () => C.rule, vLineColor: () => C.rule,
              hLineWidth: () => 0.5, vLineWidth: () => 0.5,
            },
          },
          { text: "Digital sign-ons recorded in the Coretel operations system take precedence over this printed table.", fontSize: 7, color: C.muted, margin: [0, 4, 0, 0] },
        ],
      },
    ],
  };

  const safe = (s: string) => s.replace(/[^a-z0-9-_]+/gi, "_");
  pdfMake.createPdf(dd).download(`${safe(opts.swmsNumber)}_${safe(opts.title).slice(0, 40)}_v${opts.version}.pdf`);
}
